import React, { Fragment, useState, useEffect } from 'react';
import { Breadcrumbs } from '../../../AbstractElements';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Input, FormGroup, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Users, Search, ChevronUp, ChevronDown, Download } from 'react-feather';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import CreditNotesModal from './CreditNotesModal';
import Swal from 'sweetalert2';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';

// Store names and emails are interpolated into SweetAlert's `html`, so escape them.
const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const AllCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creditNotesModalOpen, setCreditNotesModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    // IDs are handed out in creation order, so id/desc puts the newest customer first.
    const [sortBy, setSortBy] = useState('id'); // 'id' or 'store_name'
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
    const [loadError, setLoadError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/customers`, authHeader());
            // A failed load used to fall through to the empty state, so an expired
            // session read as "you have no customers" — alarming, and it hides the
            // one thing that would fix it. Say what actually happened instead.
            if ([401, 403].includes(response.status)) {
                setLoadError('Your session has expired. Redirecting you to sign in...');
                setTimeout(() => navigate('/login'), 1500);
                return;
            }
            if (!response.ok) {
                throw new Error(`Failed to fetch customers (HTTP ${response.status})`);
            }
            const data = await response.json();
            setCustomers(data);
            setLoadError(null);
        } catch (error) {
            console.error('Error fetching customers:', error);
            setLoadError(error.message || 'Could not load customers.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCustomer = () => {
        navigate(`${process.env.PUBLIC_URL}/add-customer`);
    };

    const handleEditCustomer = (customerId) => {
        // Navigate to edit customer page
        navigate(`${process.env.PUBLIC_URL}/app/customers/edit-customer/${customerId}`);
    };

    // Customers no longer appear under Users, so this is the only place their
    // portal password can be reset. Same two-way choice as the staff reset:
    // email it, or reset and read it out. The server refuses up front (503) when
    // it can't reach the mail server, so a reset never strands the customer.
    const handleResetPassword = async (customer) => {
        const canEmail = Boolean(customer.email);
        const confirm = await Swal.fire({
            title: 'Reset portal password?',
            html: `Generate a new temporary password for <strong>${escapeHtml(customer.store_name || customer.email)}</strong>.
                The current password will stop working.<br/><br/>
                ${canEmail
                    ? `Choose <strong>Reset &amp; send email</strong> and the login details will be emailed to
                       <strong>${escapeHtml(customer.email)}</strong>.`
                    : `<em>This customer has no email address, so the details can't be emailed.</em>`}`,
            icon: 'question',
            showCancelButton: true,
            showDenyButton: canEmail,
            confirmButtonText: canEmail ? 'Reset & send email' : 'Reset',
            denyButtonText: 'Reset without email',
            cancelButtonText: 'Cancel',
        });
        if (!confirm.isConfirmed && !confirm.isDenied) return;
        const sendEmail = canEmail && confirm.isConfirmed;

        try {
            const response = await fetch(`${API_BASE_URL}/api/customers/${customer.id}/generate-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader().headers },
                body: JSON.stringify({ sendEmail }),
            });
            const data = await response.json();
            if (!response.ok) {
                const refusedSafely = response.status === 503;
                await Swal.fire({
                    title: refusedSafely ? 'Email unavailable — password unchanged' : 'Failed to reset password',
                    text: data.error || `HTTP ${response.status}`,
                    icon: refusedSafely ? 'warning' : 'error',
                });
                return;
            }

            const credentials = `<div style="text-align:left">
                <strong>Email:</strong> ${escapeHtml(data.email || '-')}<br/>
                <strong>Temp password:</strong> <code style="font-size:16px">${escapeHtml(data.tempPassword)}</code><br/><br/>
                ${data.note ? `<small>${escapeHtml(data.note)}</small><br/><br/>` : ''}
                <small>This password is shown only once.</small></div>`;

            if (sendEmail && data.emailSent) {
                await Swal.fire({
                    title: 'Password reset & email sent',
                    html: `<div style="text-align:left">The login details were emailed to
                        <strong>${escapeHtml(data.email)}</strong>.<br/><br/></div>${credentials}`,
                    icon: 'success',
                });
            } else if (sendEmail && !data.emailSent) {
                await Swal.fire({
                    title: 'Password reset, but the email failed',
                    html: `<div style="text-align:left">The password was reset, but the email to
                        <strong>${escapeHtml(data.email)}</strong> could not be sent${data.emailError ? `: ${escapeHtml(data.emailError)}` : ''}.
                        Share the details below manually.<br/><br/></div>${credentials}`,
                    icon: 'warning',
                });
            } else {
                await Swal.fire({
                    title: 'Password reset',
                    html: `<div style="text-align:left">Share these with the customer:<br/><br/></div>${credentials}`,
                    icon: 'success',
                });
            }
        } catch (error) {
            Swal.fire({ title: 'Failed to reset password', text: error.message, icon: 'error' });
        }
    };

    const handleDeleteCustomer = async (customerId) => {
        if (window.confirm('Are you sure you want to delete this customer?')) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}`, {
                    method: 'DELETE',
                    ...authHeader(),
                });
                if (response.ok) {
                    fetchCustomers(); // Refresh the list
                } else {
                    alert('Failed to delete customer');
                }
            } catch (error) {
                console.error('Error deleting customer:', error);
                alert('Error deleting customer');
            }
        }
    };

    const handleOpenCreditNotes = (customer) => {
        setSelectedCustomer(customer);
        setCreditNotesModalOpen(true);
    };

    const handleCloseCreditNotes = () => {
        setCreditNotesModalOpen(false);
        setSelectedCustomer(null);
    };

    // Clicking the active column flips its direction; clicking the other one
    // switches to it, starting newest-first for ID and A-Z for store name.
    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder(column === 'id' ? 'desc' : 'asc');
        }
    };

    const sortIcon = (column) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc'
            ? <ChevronUp size={14} className="ms-1" />
            : <ChevronDown size={14} className="ms-1" />;
    };

    const handleDownloadCustomers = () => {
        const exportData = customers.map(customer => ({
            'ID': customer.id,
            'Store Name': customer.store_name || '',
            'VAT/EORI': customer.vat_eori || '',
            'Email': customer.email || '',
            'Tel': customer.tel || '',
            'Shipping Address': customer.shipping_address || '',
            'Billing Address': customer.billing_address || '',
            'Showroom': customer.showroom || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');

        // Auto-size columns
        const colWidths = Object.keys(exportData[0] || {}).map(key => ({ wch: Math.max(key.length, 20) }));
        worksheet['!cols'] = colWidths;

        XLSX.writeFile(workbook, `Customers_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // Filter and sort customers
    const getFilteredAndSortedCustomers = () => {
        let filtered = customers;

        // Apply search filter
        if (searchTerm) {
            filtered = customers.filter(customer => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    (customer.store_name && customer.store_name.toLowerCase().includes(searchLower)) ||
                    (customer.vat_eori && customer.vat_eori.toLowerCase().includes(searchLower)) ||
                    (customer.email && customer.email.toLowerCase().includes(searchLower)) ||
                    (customer.tel && customer.tel.toLowerCase().includes(searchLower))
                );
            });
        }

        // Apply sorting by the chosen column
        const direction = sortOrder === 'asc' ? 1 : -1;
        const sorted = [...filtered].sort((a, b) => {
            if (sortBy === 'id') {
                return (Number(a.id) - Number(b.id)) * direction;
            }
            const nameA = (a.store_name || '').toLowerCase();
            const nameB = (b.store_name || '').toLowerCase();
            return nameA.localeCompare(nameB) * direction;
        });

        return sorted;
    };

    const filteredAndSortedCustomers = getFilteredAndSortedCustomers();

    return (
        <Fragment>
            <Breadcrumbs parent="Customers" title="All Customers" mainTitle="All Customers" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5>
                                        <Users className="me-2" size={20} />
                                        Customer List
                                    </h5>
                                    <div className="d-flex gap-2">
                                        <Button color="success" onClick={handleDownloadCustomers} disabled={customers.length === 0}>
                                            <Download size={14} className="me-1" />
                                            Download Customers
                                        </Button>
                                        <Button color="primary" onClick={handleAddCustomer}>
                                            Add New Customer
                                        </Button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <FormGroup className="mb-0">
                                            <div className="position-relative">
                                                <Search
                                                    size={16}
                                                    className="position-absolute"
                                                    style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                                                />
                                                <Input
                                                    type="text"
                                                    placeholder="Search by store name, VAT/EORI, email, or tel..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{ paddingLeft: '35px' }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {loading ? (
                                    <div className="text-center p-4">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : loadError ? (
                                    <div className="text-center p-4">
                                        <p className="text-danger mb-2">{loadError}</p>
                                        <Button color="secondary" size="sm" onClick={() => { setLoading(true); fetchCustomers(); }}>
                                            Try again
                                        </Button>
                                    </div>
                                ) : customers.length === 0 ? (
                                    <div className="text-center p-4">
                                        <p>No customers found. Click "Add New Customer" to add one.</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table className="table-bordered">
                                            <thead>
                                                <tr>
                                                    <th
                                                        onClick={() => handleSort('id')}
                                                        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                        className="position-relative"
                                                    >
                                                        ID
                                                        {sortIcon('id')}
                                                    </th>
                                                    <th
                                                        onClick={() => handleSort('store_name')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                        className="position-relative"
                                                    >
                                                        Store Name
                                                        {sortIcon('store_name')}
                                                    </th>
                                                    <th>VAT/EORI</th>
                                                    <th>Email</th>
                                                    <th>Tel</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredAndSortedCustomers.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center p-4">
                                                            No customers match your search criteria.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredAndSortedCustomers.map((customer) => (
                                                    <tr key={customer.id}>
                                                        <td>{customer.id}</td>
                                                        <td>{customer.store_name || '-'}</td>
                                                        <td>{customer.vat_eori || '-'}</td>
                                                        <td>{customer.email || '-'}</td>
                                                        <td>{customer.tel || '-'}</td>
                                                        <td>
                                                            <UncontrolledDropdown>
                                                                <DropdownToggle caret color="primary" size="sm" outline>
                                                                    Actions
                                                                </DropdownToggle>
                                                                {/* Rendered into <body> so the menu isn't clipped by the table's own overflow. */}
                                                                <DropdownMenu end container="body">
                                                                    <DropdownItem onClick={() => handleEditCustomer(customer.id)}>
                                                                        <i className="fa fa-pencil me-2"></i>Edit
                                                                    </DropdownItem>
                                                                    <DropdownItem onClick={() => handleResetPassword(customer)}>
                                                                        <i className="fa fa-key me-2"></i>Reset password
                                                                    </DropdownItem>
                                                                    <DropdownItem onClick={() => handleOpenCreditNotes(customer)}>
                                                                        <i className="fa fa-file-text-o me-2"></i>Credit notes
                                                                    </DropdownItem>
                                                                    <DropdownItem divider />
                                                                    <DropdownItem className="text-danger" onClick={() => handleDeleteCustomer(customer.id)}>
                                                                        <i className="fa fa-trash me-2"></i>Delete
                                                                    </DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </td>
                                                    </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Credit Notes Modal */}
            {selectedCustomer && (
                <CreditNotesModal
                    isOpen={creditNotesModalOpen}
                    toggle={handleCloseCreditNotes}
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.store_name}
                />
            )}
        </Fragment>
    );
};

export default AllCustomers;
